import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Loader2, Check } from "lucide-react";
import { SERVICES, CONTACT } from "@/lib/data";
import { Reveal, Overline } from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import { api, toISODate, parseISO } from "@/lib/api";

const empty = {
  name: "",
  email: "",
  phone: "",
  guest_count: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/availability")
      .then((r) => setBooked(r.data.map((d) => d.date)))
      .catch(() => {});
  }, []);

  const bookedDates = booked.map(parseISO);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        ...form,
        event_date: date ? toISODate(date) : "",
      });
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm(empty);
      setDate(null);
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[var(--border-ink)] py-3 text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--gold)] outline-none transition-colors";

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 lg:py-40 bg-[var(--surface-subtle)]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline testid="contact-overline">Begin the Story</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Let's plan your <span className="italic text-[var(--gold)]">forever</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[var(--ink-2)] font-light leading-relaxed max-w-md">
              Choose a date to check our availability, then share a few details — our team will reach
              out personally.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-8 bg-[var(--surface)] border border-[var(--border-gold)] rounded-[3px] p-4 inline-block">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={[{ before: today }, ...bookedDates]}
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{ booked: "line-through text-red-400 opacity-70" }}
                data-testid="availability-picker"
              />
              <div className="flex gap-5 px-3 pb-1 text-xs text-[var(--ink-2)]">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--gold)]" /> Available
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" /> Booked
                </span>
              </div>
            </div>
          </Reveal>

          <div className="mt-10 space-y-6">
            <a
              href={`tel:${CONTACT.phone}`}
              className="flex items-center gap-4 group"
              data-testid="contact-phone-number"
            >
              <span className="h-11 w-11 rounded-full border border-[var(--border-gold)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-white transition-colors">
                <Phone size={16} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ink-3)]">Call us</div>
                <div className="font-serif text-xl text-[var(--ink)]">{CONTACT.phoneDisplay}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 rounded-full border border-[var(--border-gold)] flex items-center justify-center text-[var(--gold)]">
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ink-3)]">Based in</div>
                <div className="font-serif text-xl text-[var(--ink)]">{CONTACT.locationLine}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              className="bg-[var(--surface)] border border-[var(--border-gold)] rounded-[3px] p-7 sm:p-10 space-y-6"
              data-testid="wedding-inquiry-form"
            >
              <div className="flex items-center justify-between rounded-[2px] bg-[var(--surface-subtle)] px-4 py-3">
                <span className="text-sm text-[var(--ink-2)]">Selected date</span>
                <span className="font-serif text-lg text-[var(--ink)] flex items-center gap-2" data-testid="selected-date">
                  {date ? (
                    <>
                      <Check size={15} className="text-[var(--gold)]" />
                      {date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </>
                  ) : (
                    <span className="text-[var(--ink-3)] text-sm">Pick from the calendar →</span>
                  )}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="inquiry-input-name" />
                <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="inquiry-input-email" />
                <input className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="inquiry-input-phone" />
                <input className={inputCls} placeholder="Approx. guest count" value={form.guest_count} onChange={set("guest_count")} data-testid="inquiry-input-guests" />
                <select className={`${inputCls} sm:col-span-2`} value={form.service} onChange={set("service")} data-testid="inquiry-input-service">
                  <option value="">Service of interest</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Everything">Full-service — everything</option>
                </select>
              </div>
              <textarea
                className={`${inputCls} resize-none`}
                rows={3}
                placeholder="Tell us about your vision…"
                value={form.message}
                onChange={set("message")}
                data-testid="inquiry-input-message"
              />
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 rounded-full bg-[var(--ink)] text-[var(--bg)] px-8 py-4 text-sm tracking-wide hover:bg-[var(--gold)] transition-colors duration-400 disabled:opacity-60"
                data-testid="inquiry-form-submit-button"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Send Inquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
